import { mapState } from 'vuex';

const ALL_SECTIONS = ['Parenting', 'Child Support', 'Spousal Support', 'Property'];

export const PLAN_TO_SECTIONS_MAP = {
	// Retired plans. Existing owners still resolve through these — do not remove.
	'parenting_plan': ['Parenting', 'Child Support'],
	'financial_plan': ['Spousal Support', 'Property'],
	'comprehensive_plan': ALL_SECTIONS,
	'parenting_upgrade': ALL_SECTIONS,
	'financial_upgrade': ALL_SECTIONS,
	'divii_plan': ALL_SECTIONS,
	'guided_plan': ALL_SECTIONS,
	'mediated_plan': ALL_SECTIONS,
	'divii_to_guided_upgrade': ALL_SECTIONS,
	'divii_to_mediated_upgrade': ALL_SECTIONS,
	'guided_to_mediated_upgrade': ALL_SECTIONS
};

// Ascending tier order. Mirrored in functions/partyPlans.js — change both.
export const PLAN_RANK = {
	// Rank 0 = retired plans, never sold again.
	'parenting_plan': 0,
	'financial_plan': 0,
	'comprehensive_plan': 0,
	'parenting_upgrade': 0,
	'financial_upgrade': 0,
	'divii_plan': 1,
	'guided_plan': 2,
	'divii_to_guided_upgrade': 2,
	'mediated_plan': 3,
	'divii_to_mediated_upgrade': 3,
	'guided_to_mediated_upgrade': 3
};

// Unknown plan ranks below every tier, so it never wins a comparison.
export function planRank(plan) {
	return plan in PLAN_RANK ? PLAN_RANK[plan] : -1;
}

// Pay-the-difference SKUs, offered only on their `from` tier.
export const UPGRADE_SKUS = {
	'parenting_upgrade': { from: 'parenting_plan', to: 'comprehensive_plan' },
	'financial_upgrade': { from: 'financial_plan', to: 'comprehensive_plan' },
	'divii_to_guided_upgrade': { from: 'divii_plan', to: 'guided_plan' },
	'divii_to_mediated_upgrade': { from: 'divii_plan', to: 'mediated_plan' },
	'guided_to_mediated_upgrade': { from: 'guided_plan', to: 'mediated_plan' }
};

export function upgradeSkuFor(activePlan, targetPlan) {
	const match = Object.keys(UPGRADE_SKUS).find((sku) => {
		return UPGRADE_SKUS[sku].from === activePlan && UPGRADE_SKUS[sku].to === targetPlan;
	});
	return match || null;
}

// An upgrade SKU is a receipt for a tier, not a tier — resolve it before comparing.
export function effectiveTier(plan) {
	return UPGRADE_SKUS[plan] ? UPGRADE_SKUS[plan].to : plan;
}

// An upgrade SKU is a price charged on a tier's card, never a card of its own.
export function isSellableTier(plan) {
	return !UPGRADE_SKUS[plan] && planRank(plan) >= 1;
}

// Unrankable codes yield null, leaving every tier purchasable.
// functions/partyPlans.js differs on purpose — it reports the code for HubSpot.
export function highestOwnedTier(planCodes) {
	return planCodes.reduce((highest, code) => {
		if (!code) {
			return highest;
		}
		const tier = effectiveTier(code);
		return planRank(tier) > planRank(highest) ? tier : highest;
	}, null);
}

export default {
	mounted() {
		if (!this.subscription) {
			this.$store.dispatch('getSubscriptions', this.user);
		}
		// Also fetch payments for party users (one-time payments)
		if (this.user && !this.payments) {
			this.$store.dispatch('getPayments', this.user);
		}
		if (!this.$store.state.plansLoaded) {
			this.$store.dispatch('getPlans');
		}
	},
	computed: {
		...mapState(['user', 'userProfile', 'agreements', 'subscriptions', 'payments']),
		hasActiveSubscription() {
			let isActive = (this.userProfile.subscriptionStatus === true || this.userProfile.subscriptionStatus === 'active') ? true : false
			return isActive;
		},
		isSubscriptionCancelled() {
			return this.userProfile.subscriptionStatus === 'canceled';
		},
		activeSubscription() {
			return this.subscriptions
				? this.subscriptions.find(
						({ role, status }) =>
							role !== 'assistant_plan' && status === 'active'
				  )
				: null;
		},
		hasAssistantSubscription() {
			return !!this.assistantSubscription;
		},
		assistantSubscription() {
			const assistantSubscription = this.subscriptions
				? this.subscriptions.find(
						({ role, status }) =>
							role === 'assistant_plan' && status === 'active'
				  )
				: null;
			return assistantSubscription;
		},
		// determine how many available assistant packages they have
		hasAvailableAssistantSeats() {
			return this.hasAssistantSubscription
				? this.userProfile.assistants
					? this.userProfile.assistants &&
					  this.userProfile.assistants.length <
							this.assistantSubscription.quantity
					: true
				: false;
		},
		seatsRemaining() {
			return this.hasAssistantSubscription
				? this.userProfile.assistants
					? this.userProfile.assistants &&
					  this.userProfile.assistants.length <
							this.assistantSubscription.quantity
					: this.assistantSubscription.quantity
				: 0;
		},
		// Map of plan types to allowed sections
		planToSectionsMap() {
			return PLAN_TO_SECTIONS_MAP;
		},
		// Get allowed sections for the current active subscription plan
		allowedSectionsForCurrentPlan() {
			const activePlan = this.$store.getters.activePaymentForSeparatingParty;
			if (!activePlan) return [];
			return this.planToSectionsMap[activePlan] || [];
		},
		// Check if a specific section is allowed for the current plan
		isSectionAllowedForPlan(sectionName) {
			return this.allowedSectionsForCurrentPlan.includes(sectionName);
		}
	},
};
